'use client';

import { TrackingEvent } from '@acretrader/acremaps-analytics-events';
import { ErrorMessage } from '@hookform/error-message';
import { yupResolver } from '@hookform/resolvers/yup';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import Button, { ButtonVariant } from '@/components/button';
import SSOButton, { Providers } from '@/components/button/ssoButton/SSOButton';
import Checkbox from '@/components/checkbox';
import Divider from '@/components/divider';
import AcresLogo from '@/components/icons/AcresLogo';
import PasswordInput from '@/components/input/passwordInput';
import TextInput from '@/components/input/textInput';
import BaseModal from '@/components/modal/baseModal';
import ModalHeading from '@/components/modal/modalHeading';
import Typography from '@/components/typography';
import {
  AcresFeatureFlags,
  AUTH_ACTIONS,
  LOCAL_STORAGE_SIGN_UP_TRACKED,
} from '@/constants/constants';
import { LOGIN_URL, PLAT_MAP_URL, SIGN_UP_URL } from '@/constants/urls';
import { Message } from '@/ory/models/error';
import { LoginMethods } from '@/ory/models/submitMethods/methods';
import { useAnalytics } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/auth-provider';
import { useInitializeContext } from '@/providers/InitializeProvider';
import { useModalContext } from '@/providers/ModalProvider';
import { isValidEmail } from '@/utils/email-validation';
import handleOryResponse from '@/utils/ory/handleOryResponse';
import { checkForRedirect } from '@/utils/validRedirect';

import { SsoOidcClients } from './loginModal';

type Props = {
  /**
   * Optional function to handle closing the modal
   * Default:  Modal simple closes and nothing else
   */
  onCloseModal?: Function;
  /**
   * Optional callback function for clicking "login" instead on the signup modal
   * Default:  Pushes user to /login
   */
  onLoginClick?: Function;
  /**
   * Setting redirect to true will redirect the user when the modal is completed
   *   - If a `?from=` query parameter is present in the URL, the user will be redirected there
   *   - Otherwise, user will be redirected to the plat map
   */
  redirect?: boolean;
};

const modalHeaderDescription =
  'One account for nationwide access to sales data, land insights, and more.';
const alreadyHaveAnAccountText = 'Already have an account?';
const signupFormId = 'signup-form';

type FormData = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  policy: boolean;
};
const schema = yup.object({
  email: yup
    .string()
    .email('Must be a valid email')
    .required('Email is required'),
  firstName: yup
    .string()
    .max(25, 'First name must be 25 characters or less')
    .required('First name is required'),
  lastName: yup
    .string()
    .max(25, 'First name must be 25 characters or less')
    .required('Last name is required'),
  password: yup.string().required('Password is required'),
  policy: yup
    .boolean()
    .equals([true], 'This is a required field')
    .required('This is a required field'),
});

const initialValue: FormData = {
  email: '',
  firstName: '',
  lastName: '',
  password: '',
  policy: false,
};

const SignupModal: React.FC<Props> = ({
  onCloseModal = undefined,
  onLoginClick = undefined,
  redirect = false,
}) => {
  const { setModalContent } = useModalContext();
  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setError: setFormError,
    trigger,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: initialValue,
    resolver: yupResolver(schema),
  });
  const { orySignUp, oryLogin } = useAuth();
  const { getFeature } = useInitializeContext();

  // Object type. Key is the email domain, value is an object with OIDC provider and organization id
  // Example: { 'example.com': { provider: 'example', org_id: '123' } }
  const ssoOidcClients = getFeature(
    AcresFeatureFlags.SSO_OIDC_CLIENTS
  ) as SsoOidcClients;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Message[]>([]);
  const router = useRouter();
  const query = useSearchParams();

  const { identify, track } = useAnalytics();
  const firstName = watch('firstName');
  const lastName = watch('lastName');

  const from = checkForRedirect(router, (query?.get('from') as string) || '');

  // eslint-disable-next-line no-script-url
  if (from && from.startsWith('javascript:')) {
    router.push(SIGN_UP_URL);
  }

  const trackSignupStarted = useCallback(() => {
    track({
      data: {},
      event: TrackingEvent.SIGN_UP_STARTED,
    });
  }, [track]);

  useEffect(() => {
    trackSignupStarted();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canComplete = () => {
    if (!getValues('policy')) {
      trigger('policy');
      return false;
    }

    return true;
  };

  /** When the X button is clicked */
  const closeModal = useCallback(() => {
    if (redirect) {
      if (from && typeof from === 'string') {
        window.location.href = from;
      } else {
        router.push(PLAT_MAP_URL);
      }
      return;
    }

    if (onCloseModal) {
      onCloseModal();
      return;
    }

    setModalContent(null);
  }, [from, onCloseModal, redirect, router, setModalContent]);

  const onSubmit = useCallback(
    async (data: FormData) => {
      if (!isValidEmail(data.email)) {
        setFormError(
          'email',
          { message: 'Must be a valid email address' },
          { shouldFocus: true }
        );
        return;
      }

      const emailDomain = data.email.split('@')[1].toLowerCase();
      // Domain is in the list of SSO OIDC clients so we should redirect to the SSO login
      if (ssoOidcClients?.[emailDomain]) {
        oryLogin({
          method: LoginMethods.OIDC,
          provider: ssoOidcClients[emailDomain].provider,
        }).then((res) => {
          handleOryResponse(res, {
            onSuccess: () => {},
            setError,
            setIsLoading,
          });
        });
        return;
      }

      identify({
        email: data.email,
      });
      orySignUp({
        method: LoginMethods.PASSWORD,
        password: data.password,
        traits: {
          email: data.email,
          name: {
            first: data.firstName,
            last: data.lastName,
          },
          tos: true,
        },
      }).then((res) => {
        handleOryResponse(res, {
          onSuccess: () => {
            try {
              window.localStorage.setItem(
                LOCAL_STORAGE_SIGN_UP_TRACKED,
                'true'
              );
            } catch {
              // eslint-disable-next-line no-empty
            }

            // Handle redirecting afterwards
            if (redirect) {
              if (from && typeof from === 'string') {
                window.location.href = from;
              } else {
                router.push(PLAT_MAP_URL);
              }
            } else {
              closeModal();
            }
          },
          setError,
          setIsLoading,
        });
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [closeModal, from, identify, orySignUp, setFormError, track]
  );

  const goToLogin = useCallback(() => {
    if (onLoginClick) {
      onLoginClick();
    } else {
      router.push(`${LOGIN_URL}?from=${from}`);
    }
  }, [onLoginClick, from, router]);

  return (
    <BaseModal
      Content={
        <div className="-mt-2 flex flex-col">
          <Typography className="mb-6 text-gray-100" variant="h4">
            {modalHeaderDescription}
          </Typography>
          <div className="flex flex-col items-center justify-center gap-y-4">
            <SSOButton
              canSubmit={() => canComplete()}
              firstName={firstName}
              lastName={lastName}
              location="register"
              provider={Providers.GOOGLE}
              setError={setError}
              setIsLoading={setIsLoading}
              traits={{
                tos: true,
              }}
            />
            <SSOButton
              canSubmit={() => canComplete()}
              firstName={firstName}
              lastName={lastName}
              location="register"
              provider={Providers.APPLE}
              setError={setError}
              setIsLoading={setIsLoading}
              traits={{
                tos: true,
              }}
            />
          </div>

          {/* Or divider */}
          <div className="my-6 flex items-center gap-x-4">
            <Divider className="flex-grow" />
            <Typography className="text-gray-50" variant="body2">
              or
            </Typography>
            <Divider className="flex-grow" />
          </div>

          {/* Form part */}
          <form id={signupFormId} onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-x-4 sm:flex-row">
              <TextInput
                required
                control={control}
                label="First Name"
                name="firstName"
                placeholder="First Name"
              />
              <TextInput
                required
                control={control}
                label="Last Name"
                name="lastName"
                placeholder="Last Name"
              />
            </div>
            <TextInput
              required
              className="w-full"
              control={control}
              label="Email"
              name="email"
              placeholder="Email"
            />
            <PasswordInput
              required
              className="w-full"
              control={control}
              label="Password"
              name="password"
              placeholder="Password"
              type="password"
            />

            {/* Checkbox for TOS and Privacy Policy */}
            <div className="checkbox-selector flex items-start gap-x-2">
              <div className="p-1">
                <Checkbox
                  id="policy"
                  {...register('policy')}
                  onChange={(e) => {
                    setValue('policy', e.target.checked);
                  }}
                />
              </div>
              <label className="flex gap-x-2" htmlFor="policy">
                <Typography className="select text-gray-70" variant="body1">
                  {' '}
                  I agree to the Acres{' '}
                  <Link
                    passHref
                    className="text-green-50"
                    href="/terms-of-use"
                    target="_blank"
                  >
                    Terms of Use
                  </Link>{' '}
                  and{' '}
                  <Link
                    passHref
                    className="text-green-50"
                    href="/privacy-policy"
                    target="_blank"
                  >
                    Privacy Policy
                  </Link>
                </Typography>
              </label>
            </div>
            <ErrorMessage
              errors={errors}
              name="policy"
              render={({ message }) => (
                <Typography className="text-red-utility" variant="caption">
                  {message}
                </Typography>
              )}
            />
          </form>

          {/* Errors */}
          <div>
            {error.map((e) => (
              <Typography
                key={e.id}
                className="text-red-utility"
                variant="body3"
              >
                {e.text}
              </Typography>
            ))}
          </div>
        </div>
      }
      Footer={
        <div className="flex flex-col-reverse gap-y-2 sm:flex-row sm:justify-between">
          <div className="flex items-center gap-x-1">
            <Typography className="text-gray-70" variant="body2">
              {alreadyHaveAnAccountText}
            </Typography>

            <button type="button" onClick={goToLogin}>
              <Typography className="text-green-50 underline" variant="body2">
                {AUTH_ACTIONS.LOGIN}
              </Typography>
            </button>
          </div>

          <Button
            form={signupFormId}
            isLoading={isLoading}
            type="submit"
            variant={ButtonVariant.PRIMARY}
          >
            {AUTH_ACTIONS.CREATE_ACCOUNT}
          </Button>
        </div>
      }
      Header={
        <ModalHeading
          icon={<AcresLogo className="h-10" />}
          title=""
          onXClick={closeModal}
        />
      }
      className="max-w-[560px]"
    />
  );
};

export default SignupModal;
